import type { GattCharacteristic, GattServer } from '@/ble/types';
import { subscribe, writeChar } from '@/ble/gatt';
import { UUID } from '@/ble/uuids';
import { WriteQueue } from '@/ble/writeQueue';
import type { AnyTrainer, SimParams } from '@/trainer/types';
import { BleTrainerBase } from '../bleTrainerBase';
import { CrankCadence, parseCyclingPower } from '../cps/protocol';
import {
  encodeErg,
  encodeGrade,
  encodeSimInit,
  encodeUnlock,
  encodeWheelCircumference,
  encodeWindSpeed,
  parseWahooResponse,
  WAHOO_OP,
} from './protocol';
import { ERG_MIN_INTERVAL_MS, SIM_MIN_INTERVAL_MS } from '../ftms/FtmsTrainer';

/** Wahoo legacy trainer control (only when FTMS is missing). PROTOCOLS.md §3. */
export class WahooLegacyTrainer extends BleTrainerBase implements AnyTrainer {
  readonly kind = 'wahoo-legacy' as const;
  private control: GattCharacteristic | null = null;
  private queue: WriteQueue | null = null;
  private crank = new CrankCadence();
  private massKg = 84;
  private wheelM = 2.105;
  private lastInit: { crr: number; cw: number; mass: number } | null = null;
  private lastWind: number | null = null;
  private lastWheelRevs: number | null = null;
  private lastWheelTime: number | null = null;

  protected async setup(server: GattServer): Promise<void> {
    const cps = await server.getPrimaryService(UUID.CPS);
    const meas = await cps.getCharacteristic(UUID.CPS_MEASUREMENT);
    this.unsubs.push(await subscribe(meas, (dv) => this.onPower(dv)));
    this.control = await cps.getCharacteristic(UUID.WAHOO_TRAINER);
    const control = this.control;
    this.queue = new WriteQueue({
      awaitResponse: true,
      responseTimeoutMs: 1500,
      send: async (bytes) => {
        this.log('tx', bytes);
        await writeChar(control, bytes, true);
      },
      onLog: (m) => this.log('--', null, m),
    });
    this.unsubs.push(
      await subscribe(control, (dv) => {
        const r = parseWahooResponse(dv);
        this.log('rx', dv, r ? `op=0x${r.op.toString(16)} ${r.ok ? 'ok' : 'fail'}` : '');
        if (r) this.queue?.handleResponse(r.op, r.ok);
      }),
    );
    this.lastInit = null;
    this.lastWind = null;
    await this.queue.enqueue('unlock', encodeUnlock(), { responseKey: WAHOO_OP.UNLOCK, priority: 10 });
    void this.queue.enqueue('wheel', encodeWheelCircumference(this.wheelM), {
      responseKey: WAHOO_OP.WHEEL_CIRCUMFERENCE,
    });
  }

  protected onTransportLost(): void {
    this.queue?.clear();
    this.queue = null;
    this.control = null;
    this.lastInit = null;
  }

  async setUserConfig(cfg: { riderKg: number; bikeKg: number; wheelCircumferenceM: number }): Promise<void> {
    this.massKg = cfg.riderKg + cfg.bikeKg;
    this.wheelM = cfg.wheelCircumferenceM;
    this.lastInit = null;
  }

  async setErg(watts: number): Promise<void> {
    this.lastInit = null; // leaving sim mode; re-init on next sim command
    await this.queue?.enqueue('erg', encodeErg(watts), {
      responseKey: WAHOO_OP.ERG,
      minIntervalMs: ERG_MIN_INTERVAL_MS,
    });
  }

  async setSim(p: SimParams): Promise<void> {
    const q = this.queue;
    if (!q) return;
    const init = { crr: p.crr, cw: p.cwKgPerM, mass: this.massKg };
    if (
      !this.lastInit ||
      Math.abs(this.lastInit.crr - init.crr) > 1e-5 ||
      Math.abs(this.lastInit.cw - init.cw) > 1e-3 ||
      this.lastInit.mass !== init.mass
    ) {
      this.lastInit = init;
      void q.enqueue('siminit', encodeSimInit(init.mass, init.crr, init.cw), {
        responseKey: WAHOO_OP.SIM_INIT,
      });
    }
    if (this.lastWind === null || Math.abs(this.lastWind - p.windMps) > 0.01) {
      this.lastWind = p.windMps;
      void q.enqueue('wind', encodeWindSpeed(p.windMps), { responseKey: WAHOO_OP.WIND_SPEED });
    }
    await q.enqueue('grade', encodeGrade(p.gradePct), {
      responseKey: WAHOO_OP.GRADE,
      minIntervalMs: SIM_MIN_INTERVAL_MS,
    });
  }

  private onPower(dv: DataView): void {
    const m = parseCyclingPower(dv);
    const now = Date.now();
    const patch: { powerW: number; cadenceRpm?: number; speedKmh?: number } = {
      powerW: Math.max(0, m.powerW),
    };
    if (m.crankRevs !== undefined && m.lastCrankEventTime !== undefined) {
      patch.cadenceRpm = this.crank.update(m.crankRevs, m.lastCrankEventTime, now);
    } else {
      patch.cadenceRpm = this.crank.current(now);
    }
    if (m.wheelRevs !== undefined && m.lastWheelEventTime !== undefined) {
      if (this.lastWheelRevs !== null && this.lastWheelTime !== null) {
        const dRevs = m.wheelRevs - this.lastWheelRevs;
        const dT = ((m.lastWheelEventTime - this.lastWheelTime + 0x10000) % 0x10000) / 2048;
        if (dRevs >= 0 && dT > 0) patch.speedKmh = ((dRevs * this.wheelM) / dT) * 3.6;
      }
      this.lastWheelRevs = m.wheelRevs;
      this.lastWheelTime = m.lastWheelEventTime;
    }
    this.emitData(patch);
  }
}
